import Reflux from "reflux";
import moment from "moment";
import _ from "lodash";
import CatalogueService from "../services/CatalogueService";
import * as TrackingUtil from "../utils/TrackingUtil";
import TimeUtil from "../utils/TimeUtil";
import { CatalogueGroupData } from "../datatypes/CatalogueInformation";
import CustomerStore from "./CustomerStore";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import Customer from "../datatypes/customer/Customer";

type CatalogueStoreState = {
    storeId: string | null | undefined;
    country: string | null | undefined;
    customerFoodType: string;
    catalogueGroups: CatalogueGroupData[] | null | undefined;
    isLoadingCatalogues: boolean;
    lastAttemptFailed: boolean;
    lastRequestTime: Object | null | undefined;
    requestCount: number;
};

let currentState: CatalogueStoreState;

const CatalogueStore = Reflux.createStore({
    init() {
        currentState = this.getEmptyState();
        this.listenTo(CustomerStore, this.onCustomerUpdated);
    },

    getEmptyState(): CatalogueStoreState {
        return {
            storeId: null,
            country: null,
            catalogueGroups: null,
            isLoadingCatalogues: false,
            customerFoodType: "Non-Food",
            lastAttemptFailed: false,
            lastRequestTime: null,
            requestCount: 0
        };
    },

    getInitialState() {
        return {
            catalogueGroups: currentState.catalogueGroups,
            isLoadingCatalogues: currentState.isLoadingCatalogues,
            lastAttemptFailed: currentState.lastAttemptFailed,
            lastRequestTime: currentState.lastRequestTime
        };
    },

    getFoodType(foodIndicator: boolean | null | undefined): string {
        if (foodIndicator) {
            return "Food";
        }
        return "Non-Food";
    },

    onCustomerUpdated(customer: any) {
        this.getCatalogues(customer);
    },

    getCatalogues(customer?: Customer) {
        const customerId = SessionStore.getCustomerId();
        if (customerId && !customer) {
            // customer info still not available
            return { isLoadingCatalogues: true };
        }
        const foodType = this.getFoodType(customer?.food);
        const storeId = SessionStore.getCustomerStore();
        const country = SessionStore.getCountry();

        if (this.shouldPerformNewRequest(country, storeId, foodType, currentState)) {
            this.requestCatalogues(country, storeId, foodType);
        }

        return {
            isLoadingCatalogues: currentState.isLoadingCatalogues,
            catalogueGroups: currentState.catalogueGroups,
            lastAttemptFailed: currentState.lastAttemptFailed
        };
    },

    shouldPerformNewRequest(country: string, storeId: string, customerFoodType: string, state: { [x: string]: any }) {
        return (
            !state.isLoadingCatalogues &&
            (state.country !== country ||
                state.storeId !== storeId ||
                state.customerFoodType !== customerFoodType ||
                TimeUtil.moreThanAmountOfMinutesAgo(state.lastRequestTime, 5))
        );
    },

    requestCatalogues(country: string, storeId: string, foodType: string) {
        const newCount = currentState.requestCount + 1;
        currentState = _.assign(currentState, {
            isLoadingCatalogues: true,
            country: country,
            storeId: storeId,
            customerFoodType: foodType,
            lastRequestTime: moment(),
            requestCount: newCount
        });
        this.trigger();
        if (country === "FR") {
            CatalogueService.getCataloguesForAllStores(country, foodType)
                .then((response: any) => {
                    this.handleResponse(response, newCount);
                })
                .catch((e: any) => this.handleError(e));
        } else {
            CatalogueService.getCatalogues(country, storeId, foodType)
                .then((response: any) => {
                    this.handleResponse(response, newCount);
                })
                .catch((e: any) => this.handleError(e));
        }
    },

    handleResponse(response: { [x: string]: any }, reqNo: number) {
        const data = response.data;
        if (data && reqNo >= currentState.requestCount) {
            currentState = _.assign(currentState, {
                catalogueGroups: data.groups,
                isLoadingCatalogues: false,
                lastAttemptFailed: false
            });
        } else if (reqNo >= currentState.requestCount) {
            TrackingUtil.trackUIError("CatalogueStore", "Response from catalogue service is broken.", response);
            currentState = _.assign(currentState, {
                catalogueGroups: [],
                isLoadingCatalogues: false,
                lastAttemptFailed: false
            });
        } else {
            console.log("Error: request number out of sync", reqNo, currentState.requestCount);
        }

        this.trigger();
    },

    handleError(e: unknown) {
        currentState.isLoadingCatalogues = false;
        currentState.lastAttemptFailed = true;
        this.trigger();
        TrackingUtil.trackUIError("CatalogueStore", "Loading of catalogues failed.", e);
    },

    getCatalogueData() {
        return currentState;
    }
});

export default CatalogueStore;
